import { useState } from 'react';
import { Loader2, Plus, Trash2, UserPlus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toaster';
import {
  useProjectChatSettings,
  useAddProjectChatOverride,
  useUpdateProjectChatOverride,
  useRemoveProjectChatOverride,
  useCompanyChatSettings,
  useAddCompanyChatMember,
  useUpdateCompanyChatMember,
  useRemoveCompanyChatMember,
} from '@/hooks/use-chats';
import { useProjectMembers } from '@/hooks/use-projects';
import { useUsers } from '@/hooks/use-users';
import type {
  ProjectChatSettings,
  CompanyChatSettings,
  ProjectChatPermissionOverride,
  CompanyChatMember,
  ProjectMember,
  GlobalRole,
} from '@/types';

function mutationErrorDescription(error: unknown, fallback: string): string {
  const e = error as {
    response?: { data?: { message?: string } };
    message?: string;
  };
  return e.response?.data?.message ?? e.message ?? fallback;
}

/** Company branch user row (matches useUsers list item). */
type CompanyUserListRow = {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string | null;
  role: GlobalRole;
};

type AvailableUserForPicker = ProjectMember | CompanyUserListRow;

type ChatSettingsMemberRow = ProjectChatPermissionOverride | CompanyChatMember;

function pickerRowToSelectUser(row: AvailableUserForPicker): {
  id: string;
  displayName: string;
} {
  if ('userId' in row) {
    return { id: row.user.id, displayName: row.user.displayName };
  }
  return { id: row.id, displayName: row.displayName };
}

interface ChatSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chatId: string;
  chatType: 'project' | 'company';
  projectId?: string;
}

export function ChatSettingsDialog({
  open,
  onOpenChange,
  chatId,
  chatType,
  projectId,
}: ChatSettingsDialogProps) {
  const [showAddMember, setShowAddMember] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');

  // Fetch data based on chat type
  const projectSettings = useProjectChatSettings(chatType === 'project' ? chatId : '');
  const companySettings = useCompanyChatSettings(chatType === 'company' ? chatId : '');

  const addProjectOverride = useAddProjectChatOverride(chatId);
  const updateProjectOverride = useUpdateProjectChatOverride(chatId);
  const removeProjectOverride = useRemoveProjectChatOverride(chatId);

  const addCompanyMember = useAddCompanyChatMember(chatId);
  const updateCompanyMember = useUpdateCompanyChatMember(chatId);
  const removeCompanyMember = useRemoveCompanyChatMember(chatId);

  // Get available users - only fetch when dialog is open
  const projectMembers = useProjectMembers(chatType === 'project' && open ? (projectId || '') : '');
  const allUsers = useUsers();

  const settings = chatType === 'project' ? projectSettings.data : companySettings.data;
  const isLoading = chatType === 'project' ? projectSettings.isLoading : companySettings.isLoading;

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleAddMember = async () => {
    if (!selectedUserId) return;

    try {
      if (chatType === 'project') {
        await addProjectOverride.mutateAsync({
          userId: selectedUserId,
          canRead: true,
          canWrite: true,
        });
      } else {
        await addCompanyMember.mutateAsync({
          userId: selectedUserId,
          canWrite: true,
        });
      }

      toast({
        title: 'Member added',
        description: 'The user has been added to the chat.',
      });
      setSelectedUserId('');
      setShowAddMember(false);
    } catch (error: unknown) {
      console.error('Failed to add member:', error);
      toast({
        title: 'Failed to add member',
        description: mutationErrorDescription(error, 'An error occurred'),
        variant: 'destructive',
      });
    }
  };

  const handleToggleWrite = async (userId: string, canWrite: boolean) => {
    if (chatType === 'project') {
      await updateProjectOverride.mutateAsync({
        userId,
        data: { canWrite },
      });
    } else {
      await updateCompanyMember.mutateAsync({
        userId,
        data: { canWrite },
      });
    }
  };

  const handleRemoveMember = async (userId: string) => {
    try {
      if (chatType === 'project') {
        await removeProjectOverride.mutateAsync(userId);
      } else {
        await removeCompanyMember.mutateAsync(userId);
      }
      toast({
        title: 'Member removed',
        description: 'The user has been removed from the chat.',
      });
    } catch (error: unknown) {
      console.error('Failed to remove member:', error);
      toast({
        title: 'Failed to remove member',
        description: mutationErrorDescription(error, 'An error occurred'),
        variant: 'destructive',
      });
    }
  };

  // Get members/overrides based on chat type
  const members: ChatSettingsMemberRow[] =
    chatType === 'project'
      ? (settings as ProjectChatSettings | undefined)?.overrides ?? []
      : (settings as CompanyChatSettings | undefined)?.members ?? [];

  const projectChatSettings =
    chatType === 'project' ? (settings as ProjectChatSettings | undefined) : undefined;

  // Get available users to add
  const availableUsers: AvailableUserForPicker[] =
    chatType === 'project'
      ? (projectMembers.data ?? []).filter(
          (m: ProjectMember) => !members.some((o) => o.userId === m.userId)
        )
      : (allUsers.data ?? []).filter(
          (u) => u.role !== 'client' && !members.some((m) => m.userId === u.id)
        );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Chat Settings</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Default Access (Project chats only) */}
            {projectChatSettings?.defaultAccess && (
              <div>
                <h4 className="text-sm font-medium mb-3">Default Access by Role</h4>
                <div className="space-y-2 text-sm">
                  {(
                    Object.entries(
                      projectChatSettings.defaultAccess
                    ) as [
                      string,
                      { canRead: boolean; canWrite: boolean },
                    ][]
                  ).map(([role, access]) => (
                    <div
                      key={role}
                      className="flex items-center justify-between py-1"
                    >
                      <span className="capitalize">{role}</span>
                      <span
                        className={
                          access.canRead
                            ? 'text-green-500'
                            : 'text-muted-foreground'
                        }
                      >
                        {access.canRead
                          ? access.canWrite
                            ? 'Read & Write'
                            : 'Read Only'
                          : 'No Access'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Members/Overrides */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium">
                  {chatType === 'project' ? 'Permission Overrides' : 'Members'}
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAddMember(true)}
                >
                  <UserPlus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>

              {showAddMember && (
                <div className="flex gap-2 mb-4">
                  <Select
                    value={selectedUserId}
                    onValueChange={setSelectedUserId}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select user..." />
                    </SelectTrigger>
                    <SelectContent>
                      {(chatType === 'company' ? allUsers.isLoading : projectMembers.isLoading) ? (
                        <div className="p-2 text-sm text-muted-foreground">Loading users...</div>
                      ) : availableUsers.length === 0 ? (
                        <div className="p-2 text-sm text-muted-foreground">No users available to add</div>
                      ) : (
                        availableUsers.map((item) => {
                          const user = pickerRowToSelectUser(item);
                          return (
                            <SelectItem key={user.id} value={user.id}>
                              {user.displayName}
                            </SelectItem>
                          );
                        })
                      )}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleAddMember}
                    disabled={
                      !selectedUserId ||
                      addProjectOverride.isPending ||
                      addCompanyMember.isPending
                    }
                    size="icon"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              )}

              {members.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {chatType === 'project'
                    ? 'No permission overrides set. Users follow default role access.'
                    : 'No members yet. Add users to this chat.'}
                </p>
              ) : (
                <div className="border rounded-lg">
                  <ScrollArea className="h-64">
                    <div className="space-y-3 p-2">
                      {members.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            {member.user.avatarUrl ? (
                              <AvatarImage src={member.user.avatarUrl} />
                            ) : (
                              <AvatarFallback className="text-xs">
                                {getInitials(member.user.displayName)}
                              </AvatarFallback>
                            )}
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium">
                              {member.user.displayName}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {member.user.email}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1.5">
                            <Switch
                              checked={member.canWrite}
                              onCheckedChange={(checked) =>
                                handleToggleWrite(member.userId, checked)
                              }
                              disabled={
                                updateProjectOverride.isPending ||
                                updateCompanyMember.isPending
                              }
                            />
                            <Label className="text-xs">Write</Label>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => handleRemoveMember(member.userId)}
                            disabled={
                              removeProjectOverride.isPending ||
                              removeCompanyMember.isPending
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
